import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import BaseMap, { pinIcon } from '../components/map/BaseMap';
import { FilterPill } from '../components/common/ui';
import JobService from '../services/JobService';
import CategoryService from '../services/CategoryService';
import { useToast } from '../contexts/ToastContext';
import { STATUS, STATUS_ORDER } from '../styles/tokens';
import { emapaUrl } from '../utils/geo';
import { usePageRefresh } from '../components/layout/pullToRefresh';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** All located jobs on one map: "where have we measured". */
export default function MapPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const mapRef = useRef(null);
    const layerRef = useRef(null);
    const fittedRef = useRef(false);
    const [statuses, setStatuses] = useState([]);
    const [categoryId, setCategoryId] = useState('');
    const [colorBy, setColorBy] = useState('category');
    const [categories, setCategories] = useState([]);
    const [data, setData] = useState(null);
    // Full screen: the same map (and the filter bar) grows over the whole window; view and zoom are kept.
    const [full, setFull] = useState(false);
    // Phones in full screen: only the status pills (one scrolling row) and a close icon, so the map gets the space.
    const phone = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const slim = full && phone;
    useEffect(() => {
        const id = setTimeout(() => mapRef.current?.invalidateSize(), 50);
        if (!full) return () => clearTimeout(id);
        const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
        window.addEventListener('keydown', onKey);
        return () => { clearTimeout(id); window.removeEventListener('keydown', onKey); };
    }, [full]);

    useEffect(() => { CategoryService.list().then(setCategories).catch(() => {}); }, []);

    const load = useCallback(() => {
        return JobService.map({ status: statuses.length ? statuses : undefined, categoryId: categoryId || undefined })
            .then(setData)
            .catch(() => showToast(t('common.loadError'), 'error'));
    }, [statuses, categoryId, showToast, t]);
    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    // Popup links navigate inside the app without a full page reload.
    useEffect(() => {
        const onClick = (e) => {
            const a = e.target.closest?.('a[data-job]');
            if (a) { e.preventDefault(); navigate(`/jobs/${a.dataset.job}`); }
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [navigate]);

    // Draw pins whenever data, colouring or language changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !data) return;
        if (layerRef.current) layerRef.current.remove();
        const group = L.featureGroup();
        data.pins.forEach(p => {
            const color = colorBy === 'status' ? STATUS[p.status].color : (p.category?.color || '#64748B');
            const where = [p.cadastralZone, p.parcelNumber && `${t('jobs.parcelShort')} ${p.parcelNumber}`].filter(Boolean).join(', ');
            const html = `
                <div style="font-family:Montserrat,sans-serif;min-width:200px">
                  <div style="font-size:11px;color:#94A3B8;font-weight:700">${[p.number, p.category?.name].filter(Boolean).map(esc).join(' · ')}</div>
                  <div style="font-size:14px;font-weight:700;color:#0F172A;margin:2px 0">${esc(p.title)}</div>
                  <div style="font-size:12.5px;color:#475569">${esc(p.clientName)}</div>
                  ${where ? `<div style="font-size:12px;color:#64748B;margin-top:2px">${esc(where)}</div>` : ''}
                  <div style="margin-top:6px;font-size:12px;font-weight:700;color:${STATUS[p.status].color}">● ${esc(t(`status.${p.status}`))}</div>
                  <div style="display:flex;gap:12px;margin-top:8px;font-size:12.5px;font-weight:700">
                    <a href="/jobs/${p.id}" data-job="${p.id}" style="color:#427898">${esc(t('map.openJob'))}</a>
                    <a href="${emapaUrl(p.latitude, p.longitude)}" target="_blank" rel="noopener noreferrer" style="color:#B91C1C">${esc(t('map.openEmapa'))} ↗</a>
                  </div>
                </div>`;
            L.marker([p.latitude, p.longitude], { icon: pinIcon(color, 16), title: p.title }).bindPopup(html).addTo(group);
        });
        group.addTo(map);
        layerRef.current = group;
        // Fit to the pins the first time only, so filtering does not keep jumping the view.
        if (!fittedRef.current && data.pins.length) {
            map.fitBounds(group.getBounds().pad(0.2), { maxZoom: 16 });
            fittedRef.current = true;
        }
    }, [data, colorBy, t, i18n.language]);

    const toggleStatus = (s) => setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

    return (
        <Layout>
            <PageHeader
                title={t('map.title')}
                subtitle={data ? t('map.summary', { count: data.pins.length, missing: data.withoutLocation }) : t('map.subtitle')}
            />
            <Box sx={full
                ? { position: 'fixed', inset: 0, zIndex: 1300, bgcolor: '#fff', display: 'flex', flexDirection: 'column' }
                : { position: 'relative' }}>
            <Box sx={{
                bgcolor: '#fff', display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center',
                ...(full
                    ? { px: 1.5, py: 1, pt: 'calc(8px + env(safe-area-inset-top, 0px))', borderBottom: '1px solid #E2E8F0' }
                    : { border: '1px solid #E2E8F0', borderRadius: '14px', p: 1.5, mb: 2 }),
            }}>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: slim ? 'nowrap' : 'wrap', ...(slim ? { flex: 1, minWidth: 0, overflowX: 'auto', '& > *': { flexShrink: 0 } } : {}) }}>
                    <FilterPill label={t('common.all')} active={statuses.length === 0} onClick={() => setStatuses([])} />
                    {STATUS_ORDER.map(s => <FilterPill key={s} label={t(`status.${s}`)} color={STATUS[s].color} active={statuses.includes(s)} onClick={() => toggleStatus(s)} />)}
                </Box>
                {slim && (
                    <IconButton onClick={() => setFull(false)} aria-label={t('map.exitFullScreen')} sx={{ bgcolor: '#427898', color: '#fff', '&:hover': { bgcolor: '#36657f' } }}>
                        <CloseFullscreenIcon fontSize="small" />
                    </IconButton>
                )}
                {!slim && <TextField select size="small" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} sx={{ minWidth: 180 }} slotProps={{ select: { displayEmpty: true } }}>
                    <MenuItem value="">{t('jobs.filters.allCategories')}</MenuItem>
                    {categories.map(c => (
                        <MenuItem key={c.id} value={c.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: c.color }} />{c.name}</Box>
                        </MenuItem>
                    ))}
                </TextField>}
                {!slim && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { md: 'auto' } }}>
                    <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>{t('map.colorBy')}</Typography>
                    <ToggleButtonGroup exclusive size="small" value={colorBy} onChange={(_, v) => v && setColorBy(v)}>
                        <ToggleButton value="category" sx={{ px: 1.5 }}>{t('jobs.fields.category')}</ToggleButton>
                        <ToggleButton value="status" sx={{ px: 1.5 }}>{t('jobs.fields.status')}</ToggleButton>
                    </ToggleButtonGroup>
                    {full
                        ? <Button variant="contained" disableElevation startIcon={<CloseFullscreenIcon />} onClick={() => setFull(false)}>{t('map.exitFullScreen')}</Button>
                        : <Button variant="outlined" startIcon={<OpenInFullIcon />} onClick={() => setFull(true)} sx={{ whiteSpace: 'nowrap' }}>{t('map.fullScreen')}</Button>}
                </Box>}
            </Box>
            <Box sx={{ position: 'relative', ...(full ? { flex: 1, minHeight: 0 } : {}) }}>
                <BaseMap height={full ? '100%' : 'calc(100vh - 300px)'} sx={full ? { borderRadius: 0, border: 0 } : { minHeight: 420 }}
                         onReady={(map) => { mapRef.current = map; }} />
                {!full && (
                    <Tooltip title={t('map.fullScreen')} placement="right">
                        <IconButton onClick={() => setFull(true)} aria-label={t('map.fullScreen')} size="small" sx={{
                            position: 'absolute', left: 10, bottom: 10, zIndex: 1000, bgcolor: '#fff', border: '2px solid rgba(0,0,0,0.2)',
                            borderRadius: '4px', '&:hover': { bgcolor: '#F4F4F4' },
                        }}><OpenInFullIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                )}
                {!data && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, pointerEvents: 'none' }}>
                        <CircularProgress />
                    </Box>
                )}
                {data && data.pins.length === 0 && (
                    <Box sx={{ position: 'absolute', left: '50%', top: 16, transform: 'translateX(-50%)', zIndex: 500, bgcolor: '#fff', borderRadius: '10px', px: 2, py: 1, boxShadow: '0 4px 16px rgba(15,23,42,0.15)' }}>
                        <Typography sx={{ fontSize: 13, color: '#475569' }}>{t('map.empty')}</Typography>
                    </Box>
                )}
            </Box>
            </Box>
        </Layout>
    );
}
